/**
 * Organisation location — the street address and the optional map pin that
 * tell a visitor how to reach the building in the first place.
 *
 * Two facts this module keeps apart:
 *
 *   address  — human-readable, displayable, and usable as the .ics LOCATION
 *              so calendar apps offer native directions.
 *   mapsUrl  — an opaque pasted Google Maps link pinning the ACTUAL entrance.
 *              Precision the address cannot express; in Morocco street
 *              addressing is often too imprecise to find the right gate.
 *
 * Both are optional, and `directionsUrl` below is the single owner of the
 * precedence rule between them. No surface reimplements it.
 */
import { z } from "zod/v4";

/**
 * Hosts a `mapsUrl` may point at, and the path prefix each one requires.
 *
 * This allowlist is load-bearing security, not tidiness: `mapsUrl` is typed
 * by a syndic and rendered as a link in an email Gately sends to third-party
 * guests. Unconstrained, a compromised syndic account could aim "Comment
 * venir" at any URL while borrowing Gately's From-domain credibility — an
 * open-redirect / phishing vector. `google.com` also hosts redirectors
 * (`/url?q=`), so it is additionally pinned to `/maps`.
 */
const ALLOWED_MAPS_HOSTS: {
  matches: (host: string) => boolean;
  pathPrefix?: string;
}[] = [
  {
    matches: (h) => h === "google.com" || h === "www.google.com",
    pathPrefix: "/maps",
  },
  // Country domains: maps.google.ma, maps.google.co.uk, …
  { matches: (h) => /^maps\.google\.[a-z]{2,}(\.[a-z]{2,})?$/.test(h) },
  { matches: (h) => h === "maps.app.goo.gl" },
  { matches: (h) => h === "goo.gl", pathPrefix: "/maps" },
];

const MAPS_URL_MESSAGE =
  "Lien invalide. Collez un lien Google Maps (google.com/maps, maps.app.goo.gl ou goo.gl/maps).";

/** True when `value` is an https Google Maps URL we are willing to link to. */
export function isAllowedMapsUrl(value: string): boolean {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  // https only — an http link would be downgraded in transit, and any other
  // scheme (javascript:, data:) is an XSS vector wherever this is rendered.
  if (url.protocol !== "https:") return false;
  return ALLOWED_MAPS_HOSTS.some(
    (rule) =>
      rule.matches(url.hostname.toLowerCase()) &&
      (!rule.pathPrefix || url.pathname.startsWith(rule.pathPrefix)),
  );
}

/**
 * A pasted Google Maps link. Enforced at the API boundary (not merely in the
 * form) so the allowlist cannot be bypassed by calling the mutation directly.
 */
export const mapsUrlSchema = z
  .string()
  .trim()
  .max(2000)
  .refine(isAllowedMapsUrl, { message: MAPS_URL_MESSAGE });

/** A blank/whitespace-only field means "clear it", not "validate it" — so an
 *  emptied map-pin input never trips the allowlist on its way to NULL. */
const blankToNull = (v: unknown) =>
  typeof v === "string" && v.trim() === "" ? null : v;

/** Input for organization.setLocation. Blank strings clear the field. */
export const setLocationInput = z.object({
  address: z.preprocess(blankToNull, z.string().trim().max(300).nullable()),
  mapsUrl: z.preprocess(blankToNull, mapsUrlSchema.nullable()),
});
export type SetLocationInput = z.infer<typeof setLocationInput>;

/**
 * The directions URL for an organisation, or null when it has no location.
 *
 * Precedence: an explicit `mapsUrl` wins, because it pins the entrance the
 * address only approximates. Otherwise a search URL is derived from the
 * address, so every organisation that fills in an address gets a working
 * link for free and the pin stays an upgrade rather than a prerequisite.
 */
export function directionsUrl(org: {
  address: string | null;
  mapsUrl: string | null;
}): string | null {
  const pin = org.mapsUrl?.trim();
  if (pin) return pin;
  const address = org.address?.trim();
  if (!address) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}
